import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PhoneIcon, 
  UserIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const LeadDetails = ({ user }) => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    alternative_mobile: '',
    email: '',
    source: '',
    age: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    notes: '',
    action: '',
    nextAction: '',
    followUpDate: ''
  });

  const actionOptions = [
    'Connected', 'No Answer', 'Wrong Number', 'Do Not Call',
    'Interested', 'Not Interested', 'Already a Customer'
  ];

  const nextActionOptions = [
    'Follow Up', 'Converted', 'Closed'
  ];

  const loadNotes = useCallback(async () => {
    try {
      // First, let's try a simple query without joins to see if the table exists and has data
      const { data: simpleData, error: simpleError } = await supabase
        .from('lead_notes')
        .select('*')
        .eq('lead_id', parseInt(leadId));

      if (simpleError) {
        if (simpleError.message && simpleError.message.includes('does not exist')) {
          toast.error('lead_notes table does not exist. Please run the database migration script.');
          setNotes([]);
          return;
        }
        throw simpleError;
      }

      // If simple query works, try the full query with user join
      const { data, error } = await supabase
        .from('lead_notes')
        .select(`
          *,
          user:user_id (
            id,
            email
          )
        `)
        .eq('lead_id', parseInt(leadId))
        .order('created_at', { ascending: true });

      if (error) {
        // If user join fails, use simple data
        setNotes(simpleData || []);
        return;
      }
      
      setNotes(data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    }
  }, [leadId]);

  const loadLeadDetails = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();

      if (error) throw error;

      setLead(data);
      setFormData({
        name: data.name || '',
        phone: data.phone || '',
        alternative_mobile: data.alternative_mobile || '',
        email: data.email || '',
        source: data.source || '',
        age: data.age || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        notes: data.notes || '',
        action: data.action || '',
        nextAction: data.next_action || '',
        followUpDate: data.follow_up_date ? data.follow_up_date.split('T')[0] : ''
      });
    } catch (error) {
      console.error('Error loading lead details:', error);
      toast.error('Error loading lead details');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  useEffect(() => {
    if (leadId) {
      loadLeadDetails();
      loadNotes();
    }
  }, [leadId, loadLeadDetails, loadNotes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updates = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        source: formData.source,
        last_action: formData.action,
        last_action_date: new Date().toISOString(),
        status: formData.nextAction === 'Follow Up' ? 'follow_up' :
                formData.nextAction === 'Converted' ? 'converted' :
                formData.nextAction === 'Closed' ? 'closed' : lead.status,
        follow_up_date: formData.nextAction === 'Follow Up' && formData.followUpDate
                        ? new Date(formData.followUpDate).toISOString()
                        : null,
        next_action: formData.nextAction
      };

      // Add optional fields only if they have values
      if (formData.alternative_mobile) updates.alternative_mobile = formData.alternative_mobile;
      if (formData.age) updates.age = formData.age;
      if (formData.address) updates.address = formData.address;
      if (formData.city) updates.city = formData.city;
      if (formData.state) updates.state = formData.state;
      if (formData.pincode) updates.pincode = formData.pincode;
      if (formData.notes) updates.notes = formData.notes;

      const { error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId);

      if (error) throw error;

      toast.success('Lead updated successfully!');
      setIsEditing(false);
      loadLeadDetails();
    } catch (error) {
      console.error('Error saving lead details:', error);
      toast.error(`Error saving lead: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setAddingNote(true);
    try {
      // Always create a new note for conversation history
      const { error } = await supabase
        .from('lead_notes')
        .insert([{
          lead_id: parseInt(leadId), // Ensure leadId is integer
          user_id: user.id,
          note: newNote.trim()
        }])
        .select();

      if (error) {
        if (error.message && error.message.includes('does not exist')) {
          toast.error('Notes feature not set up yet. Please run the database migration script first.');
          return;
        }
        throw error;
      }

      toast.success('Note added to conversation!');
      setNewNote('');
      
      // Reload notes to show the new note
      await loadNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      if (error.message && error.message.includes('does not exist')) {
        toast.error('Notes feature not set up yet. Please run the database migration script first.');
      } else {
        toast.error(`Error saving note: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setAddingNote(false);
    }
  };

  const handleQuickAction = async (actionType) => {
    setSaving(true);
    try {
      let updates = {
        last_action: actionType,
        last_action_date: new Date().toISOString(),
      };
      let nextActionValue = '';

      if (actionType === 'Connected') {
        updates.status = 'active';
        nextActionValue = 'Follow Up';
      } else if (actionType === 'No Answer') {
        updates.status = 'pending';
        nextActionValue = 'Follow Up';
      } else if (actionType === 'Converted') {
        updates.status = 'converted';
        updates.follow_up_date = null;
        nextActionValue = 'Converted';
        
        // Fetch lead details to get address information
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('address, city, state, pincode')
          .eq('id', parseInt(leadId))
          .single();
        
        if (leadError) {
          console.error('Error fetching lead details:', leadError);
        }
        
        // Create order with auto-populated address from lead
        const { error: orderError } = await supabase
          .from('orders')
          .insert([{
            lead_id: parseInt(leadId),
            user_id: user.id,
            conversion_date: new Date().toISOString(),
            status: 'pending_shipping',
            address_line_1: leadData?.address || '',
            city: leadData?.city || '',
            state: leadData?.state || '',
            pin: leadData?.pincode || ''
          }]);
        if (orderError) throw orderError;
        
        toast.success('Lead converted and order created successfully!');
      } else if (actionType === 'Closed') {
        updates.status = 'closed';
        updates.follow_up_date = null;
        nextActionValue = 'Closed';
      }

      updates.next_action = nextActionValue;

      const { error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId);

      if (error) throw error;

      toast.success(`Lead marked as ${actionType}!`);
      loadLeadDetails();
    } catch (error) {
      console.error(`Error performing quick action ${actionType}:`, error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!lead) {
    return <div className="text-center py-8 text-gray-500">Lead not found.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/leads')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back to Leads</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-900">Lead Details</h1>
            </div>
            <div className="flex items-center space-x-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Lead
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Lead Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Lead Name</p>
                <p className="text-lg font-semibold text-gray-900">{lead.name}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Contact</p>
                <p className="text-lg font-semibold text-gray-900">{lead.phone}</p>
                {lead.alternative_mobile && (
                  <p className="text-sm text-gray-600 mt-1">Alt: {lead.alternative_mobile}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${getStatusColor(lead.status)}`}>
                  <span className="text-xs font-medium">{lead.status?.charAt(0).toUpperCase()}</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">{lead.status || 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lead Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Lead Information</h2>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      required
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      required
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="alternative_mobile" className="block text-sm font-medium text-gray-700 mb-2">
                      Alternative Mobile Number
                    </label>
                    <input
                      type="tel"
                      id="alternative_mobile"
                      name="alternative_mobile"
                      value={formData.alternative_mobile}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter alternative mobile number"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-2">
                      Source of Lead
                    </label>
                    <input
                      type="text"
                      id="source"
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="e.g., Website, Referral"
                    />
                  </div>
                </div>

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                      Age
                    </label>
                    <input
                      type="number"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      disabled={!isEditing}
                      min="1"
                      max="120"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter age"
                    />
                  </div>

                  <div>
                    <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-2">
                      Pincode
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter pincode"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows={3}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                      !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                    }`}
                    placeholder="Enter full address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter city"
                    />
                  </div>

                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                {/* Lead Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Lead Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    disabled={!isEditing}
                    rows={4}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                      !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                    }`}
                    placeholder="Enter notes about this lead..."
                  />
                </div>
              </form>
            </div>
          </div>

          {/* Follow-up Actions */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Follow-up Actions</h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Quick Actions */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleQuickAction('Connected')}
                      disabled={saving}
                      className="w-full flex items-center justify-center px-4 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4 mr-2" />
                      Mark as Connected
                    </button>
                    <button
                      onClick={() => handleQuickAction('No Answer')}
                      disabled={saving}
                      className="w-full flex items-center justify-center px-4 py-2 border border-yellow-300 rounded-md text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
                    >
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      No Answer - Follow Up
                    </button>
                    <button
                      onClick={() => handleQuickAction('Converted')}
                      disabled={saving}
                      className="w-full flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                      Convert to Order
                    </button>
                    <button
                      onClick={() => handleQuickAction('Closed')}
                      disabled={saving}
                      className="w-full flex items-center justify-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <XMarkIcon className="h-4 w-4 mr-2" />
                      Close Lead
                    </button>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* Manual Actions */}
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label htmlFor="action" className="block text-sm font-medium text-gray-700 mb-2">
                      Action Taken
                    </label>
                    <select
                      id="action"
                      name="action"
                      value={formData.action}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select Action</option>
                      {actionOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="nextAction" className="block text-sm font-medium text-gray-700 mb-2">
                      Next Action
                    </label>
                    <select
                      id="nextAction"
                      name="nextAction"
                      value={formData.nextAction}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select Next Action</option>
                      {nextActionOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {formData.nextAction === 'Follow Up' && (
                    <div>
                      <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Date
                      </label>
                      <input
                        type="date"
                        id="followUpDate"
                        name="followUpDate"
                        value={formData.followUpDate}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                    {saving ? 'Updating...' : 'Update Action'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation History Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Conversation History</h2>
            <p className="text-sm text-gray-600 mt-1">
              {notes.length} {notes.length === 1 ? 'note' : 'notes'} in this conversation
            </p>
          </div>
          
          {/* Conversation Messages */}
          <div className="max-h-[600px] overflow-y-auto">
            {notes.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ChatBubbleLeftRightIcon className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">No conversation yet</h3>
                <p className="text-sm text-gray-500">Start the conversation by adding a note below</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notes.map((note, index) => (
                  <div key={note.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {(note.user?.email || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {note.user?.email || 'User'}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Note
                            </span>
                          </div>
                          <time className="text-xs text-gray-500">
                            {new Date(note.created_at).toLocaleString()}
                          </time>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {note.note}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Add New Message */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <form onSubmit={handleAddNote}>
              <div className="flex space-x-3">
                <div className="flex-1">
                  <label htmlFor="new-note" className="sr-only">Add a note</label>
                  <textarea
                    id="new-note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note to this conversation..."
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={addingNote}
                  />
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="submit"
                    disabled={addingNote || !newNote.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingNote ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Note
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;